import { useEffect, useMemo, useState } from 'react';
import fm from 'format-message';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import media from '@activebrands/core-web/config/media';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import getSelector from '@activebrands/core-web/utils/get-selector';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import debounce from '@grebban/utils/function/debounce';
import LoadingIcon from 'components/icons/LoadingIcon';
import StateIcon from 'components/icons/StateIcon';

const Text = styled('span', {
    ...textStyles['Miscellaneous/14_100'],
    minWidth: '16px',
    textAlign: 'center',
});

const Button = styled('button', {
    height: '32px',
    width: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 0',
    background: 'var(--color-contrast-low)',
    borderRadius: '100%',

    [media.min['desktop.sm']]: {
        height: '22px',
        width: '22px',
    },

    ...getFocusRingStyles(),
});

const AmountIncrementer = ({ count, step = 1, loading = false, onChange, productName = '', selector, $style }) => {
    const [current, setCurrent] = useState(count);
    const [css] = useStyletron();

    useEffect(() => {
        if (current !== count) {
            setCurrent(count);
        }
    }, [count]);

    const debouncedFunc = useMemo(() => debounce(onChange), [onChange]);

    const disableStyle = {
        opacity: `${loading ? '0.6' : '1'}`,
        cursor: `${loading ? 'not-allowed' : 'pointer'}`,
    };

    const decrementAmount = () => {
        debouncedFunc(-step);
        setCurrent(current - step);
    };

    const incrementAmount = () => {
        debouncedFunc(step);
        setCurrent(current + step);
    };

    return (
        <div
            data-selector={selector}
            className={css({ display: 'flex', alignItems: 'center', gap: '12px', ...$style })}
        >
            <Button
                data-selector={getSelector(selector, 'minus')}
                aria-label={fm('Decrease quantity of {product} from {from} to {to}', {
                    product: productName || fm('item'),
                    from: count,
                    to: count - step,
                })}
                $style={disableStyle}
                disabled={loading}
                type="button"
                onClick={decrementAmount}
            >
                <StateIcon size={['12px', null, null, null, null, '8px']} state="minus" />
            </Button>

            {loading ? <LoadingIcon /> : <Text aria-hidden="true">{count}</Text>}

            <Button
                data-selector={getSelector(selector, 'plus')}
                aria-label={fm('Increase quantity of {product} from {from} to {to}', {
                    product: productName || fm('item'),
                    from: count,
                    to: count + step,
                })}
                $style={disableStyle}
                disabled={loading}
                type="button"
                onClick={incrementAmount}
            >
                <StateIcon size={['12px', null, null, null, null, '8px']} state="plus" />
            </Button>
        </div>
    );
};

AmountIncrementer.propTypes = {
    $style: PropTypes.object,
    count: PropTypes.number.isRequired,
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    productName: PropTypes.string,
    selector: PropTypes.string,
    step: PropTypes.number,
};

export default AmountIncrementer;
